import { useCallback, useEffect, useState, useMemo } from 'react';
import Page from '../../../../components/page/Page';
import Header from '../../../../components/operations/receptionContainers/Header';
import {
  Button,
  Chip,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import useThemeMediaQuery from '../../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../../core/router/navigation';
import { useMeCore } from '../../../../../core/modules/me';
import CreateDialog from '../../../../components/operations/receptionOrders/CreateDialog';
import UpdateDialog from '../../../../components/operations/receptionOrders/UpdateDialog';
import { useCompanyCore } from '../../../../../core/modules/company';
import { useReceptionCore } from '../../../../../core/modules/reception';
import { useParams } from 'react-router-dom';
import InventoryIcon from '@mui/icons-material/Inventory';
import PrintIcon from '@mui/icons-material/Print';
import {
  RECEPTION_TYPE_COLOR,
  RECEPTION_TYPE_LABEL,
  RECEPTION_TYPES,
} from '../../../../../core/constants/receptionTypes';
import Barcode from 'react-barcode';
import { ReceptionContainer } from '../../../../../core/interfaces/receptionCotnainer.interface';
import CopyButton from '../../../../components/shared/CopyButton';
import { usePrintLabel } from '../../../../../core/hooks/usePrintLabel';

const ReceptionContainers = () => {
  const { id } = useParams();
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const {
    receptionContainers,
    noMore,
    increasePage,
    getReceptionContainers,
    putReceptionContainer,
  } = useReceptionCore();
  const { goToWelcome } = useNavigationCore();
  const { printOriginContainer, printMultipleLabels } = usePrintLabel();

  const [editingStatus, setEditingStatus] = useState<ReceptionContainer | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

  useEffect(() => {
    if (
      !user ||
      (!user.privileges.superAdmin &&
        !user.privileges.readReception &&
        !user.privileges.writeReception)
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  useEffect(() => {
    if (companyId && id) {
      getReceptionContainers(companyId, +id);
    }
  }, [companyId, getReceptionContainers, id]);

  const updateContainerStatus = useCallback(
    (container: ReceptionContainer, status: number) => {
      if (companyId && status && id) {
        putReceptionContainer(
          companyId,
          +id,
          container.id,
          { ...container, origin_container_status_id: status },
          () => {
            getReceptionContainers(companyId, +id);
            setEditingStatus(null);
          }
        );
      }
    },
    [companyId, id, putReceptionContainer, getReceptionContainers]
  );

  const print = useCallback(
    (ev: any, container: ReceptionContainer) => {
      ev.stopPropagation();
      printOriginContainer(container);
    },
    [printOriginContainer]
  );

  // Funciones de búsqueda para pasar al Header
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Filtrar contenedores basado en el término de búsqueda
  const filteredContainers = useMemo(() => {
    if (!searchTerm.trim()) {
      return receptionContainers;
    }

    return receptionContainers.filter((container) =>
      container.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [receptionContainers, searchTerm]);

  const handlePrintAll = useCallback(() => {
    if (!filteredContainers.length) {
      return;
    }
    printMultipleLabels(filteredContainers);
  }, [filteredContainers, printMultipleLabels]);

  return (
    <>
      <Page
        header={
          <Header
            onSearch={handleSearch}
            onClearSearch={handleClearSearch}
            searchTerm={searchTerm}
            total={filteredContainers.length}
            onPrintAll={handlePrintAll}
            printDisabled={filteredContainers.length === 0}
          />
        }
        content={
          <div className="w-full my-16 sm:col-span-3 px-24 md:px-32">
            {searchTerm && (
              <div className="mb-16 px-4">
                <Typography variant="body2" color="text.secondary">
                  {filteredContainers.length > 0
                    ? `Mostrando ${filteredContainers.length} contenedor${
                        filteredContainers.length !== 1 ? 'es' : ''
                      } que coincide${
                        filteredContainers.length === 1 ? '' : 'n'
                      } con "${searchTerm}"`
                    : `No se encontraron contenedores que coincidan con "${searchTerm}"`}
                </Typography>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-24 w-full min-w-0">
              {filteredContainers.map((container) => (
                <Paper
                  className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden py-12"
                  key={container.correlative_id}
                >
                  <div className="flex flex-col flex-auto w-full p-12 items-center text-center">

                    <div className="my-12">
                      <span>{`${container.correlative_id}${
                        searchTerm
                          ? ` (${
                              filteredContainers.findIndex(
                                (c) => c.id === container.id
                              ) + 1
                            } de ${filteredContainers.length} filtrados)`
                          : ` / ${receptionContainers.length}`
                      }`}</span>
                    </div>
                    <div className="flex gap-2 justify-center">
                      {editingStatus?.id !== container.id && (
                        <Chip
                          size="small"
                          label={
                            RECEPTION_TYPE_LABEL[
                              container.origin_container_status_id
                            ]
                          }
                          color={
                            RECEPTION_TYPE_COLOR[
                              container.origin_container_status_id
                            ]
                          }
                          onClick={() => setEditingStatus(container)}
                        />
                      )}
                      {editingStatus && editingStatus.id === container.id && (
                        <Select
                          labelId="container-status-label"
                          size="small"
                          value={container.origin_container_status_id}
                          onChange={(ev) =>
                            updateContainerStatus(container, +ev.target.value)
                          }
                          required
                        >
                          {RECEPTION_TYPES.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </div>
                    {container.reference && (
                      <Typography className="font-medium text-slate-950">
                        {container.reference}
                      </Typography>
                    )}
                  </div>
                  <div className="mb-12 px-12 container-barcode text-center">
                    {/* <Chip label={container.code} size="small" /> */}

                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Typography className="text-sm text-gray-500 text-center">
                        Código de barras
                      </Typography>
                      <CopyButton
                        textToCopy={container.code}
                        tooltip="Copiar código"
                      />
                    </div>
                    <Barcode
                      value={container.code}
                      fontSize={16}
                      height={50}
                      format="CODE128"
                      margin={0}
                      displayValue={true}
                      background="#FFFFFF"
                      textAlign="center"
                    />
                  </div>
                  <Button
                    className="mx-4 my-4 w-100"
                    color="secondary"
                    onClick={(ev) => print(ev, container)}
                  >
                    <PrintIcon className="mx-2" />
                    Imprimir
                  </Button>
                </Paper>
              ))}
            </div>
            {!noMore && !searchTerm ? (
              <div
                className="text-center underline cursor-pointer my-24"
                onClick={increasePage}
              >
                Mostrar más
              </div>
            ) : null}
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <CreateDialog />
      <UpdateDialog />
    </>
  );
};

export default ReceptionContainers;
